import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { StatusDot } from '@/components/synapse/StatusDot';
import { TypePill } from '@/components/synapse/TypePill';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardActivity, useDashboardSummary } from '@/hooks/useDashboard';
import { useCan } from '@/hooks/usePermissions';
import type { PermissionKey } from '@/lib/permissions';
import { translateApiError } from '@/lib/apiError';
import { localeFor } from '@/lib/locale';
import { formatRelativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';
import type { DashboardActivity } from '@/types/api';

interface PrimaryTile {
  to: string;
  labelKey: string;
  descKey: string;
  icon: string;
  requires: PermissionKey;
}

// Every tile names the key its destination needs. A launchpad that offers an
// action the member cannot perform is worse than one that omits it: the refusal
// arrives after they have filled the form.
const PRIMARY_TILES: PrimaryTile[] = [
  {
    to: '/add',
    labelKey: 'home.addStockDesc',
    descKey: 'home.addStockDesc',
    icon: 'box_in',
    requires: 'inventory.stock.in',
  },
  {
    to: '/issue',
    labelKey: 'nav.issue',
    descKey: 'home.issueDesc',
    icon: 'box_out',
    requires: 'inventory.stock.issue',
  },
];

interface SecondaryTile {
  to: string;
  labelKey: string;
  icon: string;
  requires: PermissionKey;
}

const SECONDARY_TILES: SecondaryTile[] = [
  {
    to: '/adjust',
    labelKey: 'home.adjustment',
    icon: 'adjust',
    requires: 'inventory.stock.adjust',
  },
  {
    to: '/stock',
    labelKey: 'home.stockStatus',
    icon: 'list',
    requires: 'inventory.items.read',
  },
];

const ADMIN_TILE: SecondaryTile = {
  to: '/people',
  labelKey: 'nav.people',
  icon: 'users',
  requires: 'members.read',
};

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 5) return 'greeting.night';
  if (h < 12) return 'greeting.morning';
  if (h < 18) return 'greeting.afternoon';
  return 'greeting.evening';
}

function firstName(name: string | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

function todayLong(lang: string): string {
  return new Date().toLocaleDateString(localeFor(lang), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function HomePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const { data, isLoading, isError, error, refetch, fetchStatus } = useDashboardSummary();
  const { data: activity } = useDashboardActivity();

  const name = firstName(user?.name);
  const primary = PRIMARY_TILES.filter((tile) => can(tile.requires));
  const secondary = [...SECONDARY_TILES, ADMIN_TILE].filter((tile) => can(tile.requires));
  const showSummary = can('dashboard.read');
  const totals = data?.totals;
  const showLoading = isLoading || (!data && !isError && fetchStatus === 'idle');
  const isEmpty = !showLoading && !isError && totals !== undefined && totals.itemCount === 0;
  // A role can legitimately grant nothing this page can show — and a member can
  // end up with no roles at all when the last one they held is deleted. Saying
  // so beats a page that is simply, inexplicably, empty.
  const hasNothing = primary.length === 0 && secondary.length === 0 && !showSummary;

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
          {t(greetingKey())}
          {name ? `, ${name}` : ''}
        </h1>
        <p className="text-[13.5px] text-muted">{todayLong(i18n.language)}</p>
      </header>

      {hasNothing ? <NoAccessCard /> : null}

      {primary.length > 0 || secondary.length > 0 ? (
        <section aria-label={t('home.quickActions')} className="mb-6 sm:mb-8">
          <div className="mb-2 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
            {t('home.quickActions')}
          </div>
          {primary.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {primary.map((tile) => (
                <PrimaryTileEl key={tile.to} tile={tile} />
              ))}
            </div>
          ) : null}
          {secondary.length > 0 ? (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {secondary.map((tile) => (
                <SecondaryTileEl key={tile.to} tile={tile} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {showSummary ? (
        <section aria-label={t('home.summary')} className="mb-6 sm:mb-8">
          <div className="mb-2 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
            {t('home.atAGlance')}
          </div>
          {showLoading ? (
          <SummarySkeleton />
          ) : isError ? (
            <SummaryError error={error} onRetry={() => refetch()} />
          ) : isEmpty ? (
            <EmptyInventoryCard />
          ) : totals ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <SummaryCard
                label={t('home.outOfStock')}
                value={totals.outOfStockCount}
                tone={totals.outOfStockCount > 0 ? 'danger' : 'ok'}
                to="/stock?filter=out"
              />
              <SummaryCard
                label={t('home.expiringSoon')}
                value={totals.expiringSoonBatchCount}
                tone={totals.expiringSoonBatchCount > 0 ? 'warn' : 'ok'}
                to="/stock?filter=expiring"
                extra={
                  totals.expiredBatchCount > 0 ? (
                    <Link
                      to="/stock?filter=expired"
                      className="inline-block text-[11.5px] font-medium text-destructive underline-offset-2 hover:underline"
                    >
                      {t('home.alreadyExpired', { count: totals.expiredBatchCount })}
                    </Link>
                  ) : undefined
                }
              />
              <SummaryCard label={t('home.items')} value={totals.itemCount} tone="neutral" />
              <SummaryCard label={t('home.onHand')} value={totals.totalOnHand} tone="neutral" />
            </div>
          ) : null}
        </section>
      ) : null}

      {activity && activity.length > 0 && (
        <section aria-label={t('home.recentActivity')}>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
              {t('home.recentActivity')}
            </span>
            {/* This strip is capped at five rows server-side; the full ledger —
                and the only place an entry can be corrected — is /activity. */}
            <Link to="/activity" className="text-[11.5px] text-muted hover:text-ink">
              {t('activity.viewAll')}
            </Link>
          </div>
          <Card className="overflow-hidden p-0">
            <ul className="divide-y divide-line">
              {activity.map((m) => (
                <li key={m.id}>
                  <ActivityRow movement={m} />
                </li>
              ))}
            </ul>
          </Card>
        </section>
      )}
    </div>
  );
}

function PrimaryTileEl({ tile }: { tile: PrimaryTile }) {
  const { t } = useTranslation();
  return (
    <Link
      to={tile.to}
      className="group flex items-center gap-4 rounded-lg border border-accent bg-accent px-5 py-5 text-accent-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:py-6"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
        <Icon name={tile.icon} size={24} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[17px] font-semibold tracking-[-0.01em] sm:text-[18px]">
          {tile.to === '/add' ? t('nav.addStock') : t(tile.labelKey)}
        </div>
        <div className="text-[12.5px] opacity-80">{t(tile.descKey)}</div>
      </div>
      <Icon name="chevron_right" size={18} className="shrink-0 opacity-70" />
    </Link>
  );
}

function SecondaryTileEl({ tile }: { tile: SecondaryTile }) {
  const { t } = useTranslation();
  return (
    <Link
      to={tile.to}
      className="flex items-center gap-2.5 rounded-lg border border-line bg-paper px-4 py-3 text-ink transition-colors hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-paper-2 text-muted">
        <Icon name={tile.icon} size={16} />
      </div>
      <span className="text-[13.5px] font-medium tracking-[-0.01em]">{t(tile.labelKey)}</span>
    </Link>
  );
}

interface SummaryCardProps {
  label: string;
  value: number;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  extra?: ReactNode;
  to?: string;
}

function SummaryCard({ label, value, tone, extra, to }: SummaryCardProps) {
  const main = (
    <>
      <div className="flex items-center gap-1.5 text-[11.5px] font-medium uppercase tracking-[0.06em] text-muted">
        {tone !== 'neutral' && <StatusDot tone={tone} />}
        {label}
      </div>
      <div
        className={cn(
          'mt-1 font-mono text-[28px] font-semibold tabular-nums',
          tone === 'danger' ? 'text-destructive' : tone === 'warn' ? 'text-warn' : 'text-ink',
        )}
      >
        {value}
      </div>
    </>
  );

  if (to) {
    return (
      <div className="rounded-lg border border-line bg-paper transition-colors hover:bg-paper-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
        <Link to={to} className="block rounded-lg p-4 focus:outline-none">
          {main}
        </Link>
        {extra && <div className="px-4 pb-3">{extra}</div>}
      </div>
    );
  }
  return (
    <Card className="p-4">
      {main}
      {extra && <div className="mt-0.5">{extra}</div>}
    </Card>
  );
}

function SummarySkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" aria-busy="true" aria-live="polite">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[92px] animate-pulse rounded-lg border border-line bg-paper-2" />
      ))}
    </div>
  );
}

function SummaryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <Icon name="alert" size={22} className="text-destructive" />
      <div className="text-[13.5px] font-medium">{t('home.couldNotLoadSummary')}</div>
      <p className="text-[12.5px] text-muted">{translateApiError(error)}</p>
      <Button size="sm" onClick={onRetry}>
        {t('common.tryAgain')}
      </Button>
    </Card>
  );
}

function EmptyInventoryCard() {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="box" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">
        {t('home.emptyInventoryTitle')}
      </div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{t('home.emptyInventoryBody')}</p>
      <Button asChild>
        <Link to="/add">
          <Icon name="plus" size={16} /> {t('nav.addStock')}
        </Link>
      </Button>
    </Card>
  );
}

/**
 * Shown when this member's roles grant nothing the home page can offer.
 *
 * Reachable two ways: a role deliberately scoped to one narrow area that the
 * launchpad does not cover, and a member whose last role was deleted — the
 * server permits that, so the client has to have an answer for it. Names the
 * remedy, because there is nothing the member can do here themselves.
 */
function NoAccessCard() {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="info" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">
        {t('home.noAccessTitle')}
      </div>
      <p className="max-w-[360px] text-[12.5px] text-muted">{t('home.noAccessBody')}</p>
    </Card>
  );
}

function ActivityRow({ movement }: { movement: DashboardActivity }) {
  const { t } = useTranslation();
  const KIND_LABEL: Record<DashboardActivity['kind'], string> = {
    STOCK_IN: t('home.kindStockIn'),
    ISSUE: t('home.kindIssue'),
    ADJUSTMENT: t('home.kindAdjustment'),
  };
  const KIND_ICON: Record<DashboardActivity['kind'], string> = {
    STOCK_IN: 'box_in',
    ISSUE: 'box_out',
    ADJUSTMENT: 'adjust',
  };
  const signed = movement.quantity > 0 ? `+${movement.quantity}` : String(movement.quantity);
  const relText = formatRelativeTime(new Date(movement.createdAt), t);
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper-2 text-muted">
        <Icon name={KIND_ICON[movement.kind]} size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13.5px] font-semibold tracking-[-0.01em]">
            {movement.item.brandName}
          </span>
          <TypePill type={movement.item.type} size="sm" />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-muted">
          <span>{KIND_LABEL[movement.kind]}</span>
          {movement.reason && <span>· {movement.reason}</span>}
          {movement.actor?.name && (
            <span>
              · {t('home.by')} {movement.actor.name}
            </span>
          )}
          <span>· {relText}</span>
        </div>
      </div>
      <span
        className={cn(
          'shrink-0 font-mono text-[14px] font-semibold tabular-nums',
          movement.quantity > 0 ? 'text-success' : 'text-destructive',
        )}
      >
        {signed}
      </span>
    </div>
  );
}
